import {
  createContext,
  MutableRefObject,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useNavigate } from 'react-router'
import { useLocalStorage } from 'react-use'

import { useUserOrganization } from '@modules-deprecated/app/organization'

import { E_INVOICING_PATH_TO_STEP, E_INVOICING_STEP_TO_PATH } from '../constants/eInvoicingStepPathMapping'
import { E_INVOICING_ENTRY_URL } from '../constants/filters'
import { EInvoicingSetupStep } from '../types'
import { getEInvoicingBaseUrl } from '../utils'

export interface EInvoicingSetupContextValue {
  currentStep: MutableRefObject<EInvoicingSetupStep>
  goToPreviousStep: (currentStep: EInvoicingSetupStep, prevStepPath?: string) => void
  kycAttemptId: string | null
  setKycAttemptId: (kycAttemptId: string) => void
  stepHistory: EInvoicingSetupStep[]
  transitionToStep: (step: EInvoicingSetupStep) => void
}

export const EInvoicingSetupContext = createContext<EInvoicingSetupContextValue | null>(null)

interface EInvoicingSetupContextProviderProps {
  initialKycAttemptId?: string | null
  initialStep?: EInvoicingSetupStep
}

export const EInvoicingSetupContextProvider = ({
  initialKycAttemptId = null,
  initialStep = 'EMPTY_STATE',
  children,
}: PropsWithChildren<EInvoicingSetupContextProviderProps>) => {
  const navigate = useNavigate()
  const { organization } = useUserOrganization()
  const baseUrl = getEInvoicingBaseUrl(organization)
  const location = window.location

  const [, setEntryUrl] = useLocalStorage<string>(E_INVOICING_ENTRY_URL)

  const currentStep = useRef(initialStep)
  const [kycAttemptId, setKycAttemptId] = useState(initialKycAttemptId)
  const [stepHistory, setStepHistory] = useState<EInvoicingSetupStep[]>([initialStep])

  const stepPathFromUrl = useMemo(() => {
    const pathParts = location.pathname.split('/')
    return pathParts[pathParts.length - 1]
  }, [location.pathname])

  const transitionToStep = useCallback(
    (step: EInvoicingSetupStep) => {
      const path = E_INVOICING_STEP_TO_PATH[step]
      navigate(`${baseUrl}/${path}`)
      currentStep.current = step
      if (step !== stepHistory[stepHistory.length - 1]) {
        setStepHistory((prev) => [...prev, step])
      }
    },
    [baseUrl, navigate, stepHistory],
  )

  const goToPreviousStep = useCallback(() => {
    setStepHistory((prevHistory) => {
      if (prevHistory.length <= 1) {
        return prevHistory
      }
      const newHistory = prevHistory.slice(0, -1)
      const previousStep = newHistory[newHistory.length - 1]
      const path = E_INVOICING_STEP_TO_PATH[previousStep]
      navigate(`${baseUrl}/${path}`)
      currentStep.current = previousStep
      return newHistory
    })
  }, [baseUrl, navigate])

  useEffect(() => {
    const stepFromUrl = E_INVOICING_PATH_TO_STEP[stepPathFromUrl]
    if (stepFromUrl && currentStep.current !== stepFromUrl) {
      goToPreviousStep()
    }
  }, [goToPreviousStep, stepPathFromUrl])

  // To store the correct entry-point from which the user entered the KYC flow
  useEffect(() => {
    const isUpload = location.pathname.includes('/uploads')
    const isDashboard = location.pathname.includes('/dashboard')

    const isPotentialEntryPoint = isUpload || isDashboard

    if (isPotentialEntryPoint) {
      setEntryUrl(`${location.pathname}${location.search}`)
    }
  }, [setEntryUrl, location.pathname, location.search])

  return (
    <EInvoicingSetupContext.Provider
      value={{
        currentStep,
        goToPreviousStep,
        kycAttemptId,
        stepHistory,
        setKycAttemptId,
        transitionToStep,
      }}
    >
      {children}
    </EInvoicingSetupContext.Provider>
  )
}

export const useEInvoicingSetupContext = () => {
  const context = useContext(EInvoicingSetupContext)

  if (!context) {
    throw new Error('EInvoicingSetupContext is missing in the module!')
  }

  return context
}
